/**
 * The Rotom Phone's apps (plan §9.11): what the home screen shows, in order, and where each one lives.
 *
 * Three are real for the closed beta — Music, Map and Stats — with Settings for the theme and the session;
 * the rest are honest "coming soon" icons, never links to nothing. Staff get one more, the console, which is
 * the existing staff tooling at `/`.
 */
import type { GlyphName } from './icons';

export type PhoneAppId = 'music' | 'map' | 'stats' | 'settings' | 'quests' | 'mail' | 'tower' | 'console';

export interface PhoneApp {
  readonly id: PhoneAppId;
  readonly name: string;
  /** Where the app lives; null for one that is not built yet. */
  readonly path: string | null;
  readonly glyph: GlyphName;
  /** `soon` draws a disabled icon with a badge; `staff` is shown to a staff session only. */
  readonly availability: 'ready' | 'soon' | 'staff';
}

export const PHONE_APPS: readonly PhoneApp[] = [
  { id: 'music', name: 'Music', path: '/audio', glyph: 'music', availability: 'ready' },
  { id: 'map', name: 'Map', path: '/map', glyph: 'map', availability: 'ready' },
  { id: 'stats', name: 'Stats', path: '/me', glyph: 'stats', availability: 'ready' },
  { id: 'settings', name: 'Settings', path: '/settings', glyph: 'settings', availability: 'ready' },
  { id: 'quests', name: 'Quests', path: null, glyph: 'quests', availability: 'soon' },
  { id: 'mail', name: 'Mail', path: null, glyph: 'mail', availability: 'soon' },
  { id: 'tower', name: 'Tower', path: null, glyph: 'tower', availability: 'soon' },
  { id: 'console', name: 'Console', path: '/', glyph: 'console', availability: 'staff' },
];

/** The phone's home screen. Where every visitor who is not staff lands (`SELF_SERVICE_HOME`). */
export const PHONE_HOME = '/phone';

/** The routes drawn inside the phone rather than under the staff shell's header. */
const PHONE_PATHS = [PHONE_HOME, '/audio', '/me', '/map', '/settings', '/link'] as const;

export function isPhonePath(pathname: string): boolean {
  return PHONE_PATHS.some((path) => pathname === path || pathname.startsWith(`${path}/`));
}

/** The app a phone route belongs to, for its title bar. `/link` is the account screen Stats leads to. */
export function phoneAppFor(pathname: string): PhoneApp | null {
  return PHONE_APPS.find((app) => app.path !== null && app.path !== '/' && (pathname === app.path || pathname.startsWith(`${app.path}/`))) ?? null;
}
